import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Table from "../../../../components/Table";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import Input from "../../../../components/Input";
import InputSelect from "../../../../components/InputSelect";
import { ModalDelete } from "../../../../components/ModalDelete";

import api from "../../../../services/api";
import { renderError } from "../../../../helpers/errors";
import { flattenCategories, getCategoryLabel } from "../../helpers";

const TYPE_OPTIONS = [
    { label: 'Receita', value: 'income' },
    { label: 'Despesa', value: 'expense' },
];

/**
 * Natureza da transação (BDM-1040): outro eixo, independente da hierarquia —
 * é por ele que o relatório separa o que é da operação do que é custo de
 * dinheiro. Fica opcional porque classificar isso é decisão do contador, e
 * chutar "operacional" para todo mundo seria inventar um número.
 */
const NATURE_OPTIONS = [
    { label: 'Não classificada', value: null },
    { label: 'Operacional', value: 'operational' },
    { label: 'Financeira', value: 'financial' },
    { label: 'Investimento', value: 'investment' },
];

const getDescendantIds = (categories, id) => categories
    .filter(category => category.parent_id === id)
    .flatMap(category => [category.id, ...getDescendantIds(categories, category.id)]);

export const CategoriesTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [categoryToEdit, setCategoryToEdit] = useState(null);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [type, setType] = useState('income');
    const [nature, setNature] = useState(null);
    const [parentId, setParentId] = useState(null);

    useEffect(() => {
        getCategories();
    }, []);

    const getCategories = () => {
        setLoading(true);

        api.get(`/finance-category`).then(res => {
            setCategories(res.data.resources);
        }).catch(renderError).then(() => setLoading(false));
    };

    const openModal = category => {
        setCategoryToEdit(category);
        setCode(category?.code ?? '');
        setName(category?.name ?? '');
        setType(category?.type ?? 'income');
        setNature(category?.nature ?? null);
        setParentId(category?.parent_id ?? null);
        setShowModal(true);
    };

    // O tipo de uma subcategoria é sempre o do pai: um grupo de despesa não
    // pode ter filha de receita, senão o total do bloco não fecha.
    const handleParent = id => {
        setParentId(id);

        const parent = categories.find(category => category.id === id);

        if (parent) {
            setType(parent.type);
        }
    };

    const save = () => {
        setSaving(true);

        const data = { code: code || null, name, type, nature: nature || null, parent_id: parentId };
        const request = categoryToEdit
            ? api.put(`/finance-category/${categoryToEdit.id}`, data)
            : api.post(`/finance-category`, data);

        request.then(() => {
            toast.success('Categoria salva');
            setShowModal(false);
            getCategories();
        }).catch(renderError).then(() => setSaving(false));
    };

    const deleteCategory = () => {
        setDeleting(true);

        api.delete(`/finance-category/${categoryToDelete.id}`).then(() => {
            setCategoryToDelete(null);
            getCategories();
        }).catch(renderError).then(() => setDeleting(false));
    };

    const blockedParentIds = categoryToEdit
        ? [categoryToEdit.id, ...getDescendantIds(categories, categoryToEdit.id)]
        : [];

    const parentOptions = [
        { label: 'Nenhuma (categoria principal)', value: null },
        ...flattenCategories(categories)
            .filter(category => !blockedParentIds.includes(category.id))
            .map(category => ({
                label: `${'— '.repeat(category.depth)}${getCategoryLabel(category)}`,
                value: category.id,
            })),
    ];

    const rows = flattenCategories(categories).map(category => ({
        data: [
            <span style={{ paddingLeft: category.depth * 22, fontWeight: category.children_count > 0 ? 600 : 400 }}>
                {getCategoryLabel(category)}
            </span>,
            TYPE_OPTIONS.find(option => option.value === category.type)?.label ?? category.type,
            NATURE_OPTIONS.find(option => option.value === category.nature)?.label ?? '—',
        ],
        buttons: [
            <Button type={`delete`} svg={`delete`} action={() => setCategoryToDelete(category)} />,
        ],
        action: () => openModal(category),
    }));

    return (
        <>
            <ModalDelete
                show={categoryToDelete !== null}
                text={<p>Tem certeza que deseja apagar a categoria <strong>{categoryToDelete?.name}</strong>?</p>}
                action={deleteCategory}
                loading={deleting}
                close={() => setCategoryToDelete(null)}
            />
            <Modal
                title={categoryToEdit ? 'Editar categoria' : 'Nova categoria'}
                show={showModal}
                close={() => setShowModal(false)}
                footer={<Button type={`primary`} size={`small`} text={`Salvar`} action={save} loading={saving} />}
            >
                <div className="section">
                    <div className="row">
                        <div className="col-3">
                            <Input label={`Código`} value={code} change={setCode} />
                        </div>
                        <div className="col-9">
                            <Input label={`Nome`} value={name} change={setName} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-8">
                            <InputSelect
                                label={`Categoria pai`}
                                options={parentOptions}
                                value={parentId}
                                change={handleParent}
                            />
                        </div>
                        <div className="col-4">
                            <InputSelect
                                label={`Tipo`}
                                options={TYPE_OPTIONS}
                                value={type}
                                change={setType}
                                isDisabled={!!parentId}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <InputSelect
                                label={`Tipo de transação`}
                                options={NATURE_OPTIONS}
                                value={nature}
                                change={setNature}
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button type={`primary`} size={`small`} svg={`plus-white`} text={`Nova categoria`} action={() => openModal(null)} />
            </div>
            <Table header={['Categoria', 'Tipo', 'Tipo de transação', '']} rows={rows} loading={loading} />
        </>
    );
};

export default CategoriesTab;
